import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { AppDispatch, RootState } from "@/app/store";

import {
  clearQuote,
  clearSwapSuccess,
  resetForm,
  setQuoteExpired,
  updateFormField,
} from "../store/swapsSlice";
import {
  thunkFetchQuote,
  thunkFetchWallets,
  thunkSubmitSwap,
  type SwapQuoteParams,
} from "../store/swapsThunks";
import type { SwapCurrencyOption, SwapFormData } from "../types/swaps-api.types";

export interface UseSwapsOptions {
  /**
   * Load the wallets on mount. **Exactly one consumer per screen may set this** — the page.
   *
   * The page gates `SwapForm` behind the loading flag, and the form calls this same hook. If the form also
   * fetched on mount, it would flip `isLoading` back to true, the page would swap it for the spinner, the
   * form would unmount, the fetch would settle, the form would remount and fetch again — an endless
   * mount/unmount loop hammering `/wallets/currencies`. Fetching is the page's job; the form only reads.
   */
  autoFetch?: boolean;
}

export const useSwaps = ({ autoFetch = false }: UseSwapsOptions = {}) => {
  const dispatch = useDispatch<AppDispatch>();

  const wallets = useSelector((state: RootState) => state.swaps.wallets);
  const quote = useSelector((state: RootState) => state.swaps.quote);
  const form = useSelector((state: RootState) => state.swaps.form);
  const success = useSelector((state: RootState) => state.swaps.success);

  /**
   * Origin can only be a currency the organization already holds — there is nothing to spend from a wallet
   * that does not exist. Destination also offers the activation catalog, because the backend creates the
   * receiving wallet when the swap is redeemed.
   */
  const originOptions = useMemo<SwapCurrencyOption[]>(
    () =>
      Object.values(wallets.byCurrency)
        .map((wallet) => ({ currency: wallet.currency, wallet }))
        .sort((a, b) => a.currency.localeCompare(b.currency)),
    [wallets.byCurrency],
  );

  const destinationOptions = useMemo<SwapCurrencyOption[]>(() => {
    const currencies = new Set([...Object.keys(wallets.byCurrency), ...wallets.availableCurrencies]);
    return [...currencies]
      .map((currency) => ({ currency, wallet: wallets.byCurrency[currency] ?? null }))
      .sort((a, b) => a.currency.localeCompare(b.currency));
  }, [wallets.byCurrency, wallets.availableCurrencies]);

  const walletFor = useCallback(
    (currency: string) => (currency ? (wallets.byCurrency[currency] ?? null) : null),
    [wallets.byCurrency],
  );

  const fetchWallets = useCallback(() => dispatch(thunkFetchWallets()), [dispatch]);
  const fetchQuote = useCallback((params: SwapQuoteParams) => dispatch(thunkFetchQuote(params)), [dispatch]);
  const submitSwap = useCallback(() => dispatch(thunkSubmitSwap()), [dispatch]);

  const updateField = useCallback(
    (field: keyof SwapFormData, value: string) => dispatch(updateFormField({ field, value })),
    [dispatch],
  );
  const markQuoteExpired = useCallback(() => dispatch(setQuoteExpired(true)), [dispatch]);
  const discardQuote = useCallback(() => dispatch(clearQuote()), [dispatch]);
  const resetSwapForm = useCallback(() => dispatch(resetForm()), [dispatch]);
  const clearSuccess = useCallback(() => dispatch(clearSwapSuccess()), [dispatch]);

  useEffect(() => {
    if (!autoFetch) return;
    void fetchWallets();
  }, [autoFetch, fetchWallets]);

  return {
    // Wallets
    walletsLoading: wallets.isLoading,
    /** True once a fetch has completed. Lets the caller tell "first load" from "background refresh". */
    walletsLoaded: wallets.lastFetched !== null,
    walletsError: wallets.error,
    hasAccess: wallets.hasAccess,
    originOptions,
    destinationOptions,
    walletFor,
    fetchWallets,

    // Quote
    quoteData: quote.data,
    quoteLoading: quote.isLoading,
    quoteError: quote.error,
    quoteExpired: quote.isExpired,
    lastChangedAmountField: quote.lastChangedAmountField,
    fetchQuote,
    markQuoteExpired,
    discardQuote,

    // Form
    formData: form.formData,
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    updateField,
    submitSwap,
    resetSwapForm,

    // Success
    success,
    clearSuccess,
  };
};
